import { getBackofficeUrl } from 'utils/urls';

/** Upper bound for a promo request so a stalled connection cannot hang the UI. */
export const PROMO_REQUEST_TIMEOUT_MS = 15000;

/** POST JSON and parse the reply, aborting (AbortError) after the timeout. */
async function postJsonWithTimeout(
  url: string,
  init: RequestInit
): Promise<{ status: number; json: any }> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROMO_REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(url, { ...init, signal: controller.signal });
    let json: any = null;
    try {
      json = await res.json();
    } catch (e) {
      // A non-2xx reply (for example a gateway 401) may carry no JSON body; the
      // caller classifies it by status. An unreadable 2xx has an unknown outcome.
      if (res.status >= 200 && res.status < 300) throw e;
    }
    return { status: res.status, json };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Normalised result of a promo-code verification. `valid` gates the deposit
 * Continue button; `message` carries the human-facing reason when it is not
 * valid (expired / min-deposit / already-used / generic invalid).
 */
export type PromoVerifyResult = {
  valid: boolean;
  message: string;
  rewards: number | null;
  minDeposit: number | null;
  /** The code was already redeemed; informational rather than an error. */
  alreadyUsed?: boolean;
};

/** Shown when a request fails before a reply can be read (offline, timeout, 5xx). */
export const PROMO_CONNECTION_MESSAGE =
  'There seems to be a connection issue. Retry to confirm promo status.';

/** Shown when the backend declines a claim, or the request cannot be made. */
export const PROMO_PROBLEM_MESSAGE =
  'Sorry, there was a problem with redeeming your promo. If this issue continues, please contact support.';

/**
 * Confirmation label shown once a code applies cleanly. Includes the bonus
 * amount the backoffice granted (PromoVerifyResult.rewards) when there is one,
 * so the player sees the promo's value (e.g. "Promo code applied · $15.00
 * bonus") rather than a bare "applied".
 */
export function promoAppliedLabel(rewards: number | null): string {
  return typeof rewards === 'number' && rewards > 0
    ? `Promo code applied · $${rewards.toFixed(2)} bonus`
    : 'Promo code applied';
}

/** Confirmation shown once a History redeem has been credited. */
export function promoRedeemedLabel(rewards: number | null): string {
  if (typeof rewards !== 'number' || rewards <= 0) {
    return 'Congrats! Your promo has now been redeemed.';
  }
  const amount = Number.isInteger(rewards) ? String(rewards) : rewards.toFixed(2);
  return `Congrats! Your $${amount} promo has now been redeemed.`;
}

const INVALID_CODE_MESSAGE = 'This is not a valid promo code.';

/**
 * Interpret the `/api/promo/verify` payload into a Cashier-facing verdict.
 * Mirrors the standalone payments app (amountInputPage.handleApplyPromoCode) so
 * the in-app Cashier and the hosted card page agree on what a code means.
 *
 * The engine always replies rc==='0' for a *recognised* request and encodes the
 * real outcome in `message` ("Code Valid", "Code Expired", "…requires a minimum
 * deposit of $N", "…has been used already"); anything else — including a
 * non-zero rc — is treated as an invalid code.
 *
 * Exported separately so the branching is unit-testable without a network stub.
 */
export function interpretPromoResponse(data: any): PromoVerifyResult {
  const message: string = data?.message || '';
  const isCodeValid = data?.rc === '0' || data?.rc === 0;
  const rewards = data?.data?.rewards ?? null;
  const requiredDepositMatch = message.match(/\$(\d+)/);
  const minDeposit = requiredDepositMatch ? Number(requiredDepositMatch[1]) : null;

  const invalid = (msg: string): PromoVerifyResult => ({
    valid: false,
    message: msg,
    rewards: null,
    minDeposit
  });

  if (!isCodeValid) return invalid(INVALID_CODE_MESSAGE);

  if (message === 'Code Valid') {
    return { valid: true, message: '', rewards, minDeposit };
  }
  if (message === 'Code Expired') return invalid('This promo code has expired.');
  if (message.includes('requires a minimum deposit')) {
    return invalid(
      minDeposit !== null
        ? `This promo code requires a minimum deposit of $${minDeposit}.`
        : 'This promo code requires a higher minimum deposit.'
    );
  }
  if (message.includes('has been used already')) {
    return { ...invalid('This promo code has already been redeemed.'), alreadyUsed: true };
  }
  return invalid(INVALID_CODE_MESSAGE);
}

export type RedeemState = 'redeem' | 'redeemed' | null;

const CLAIMABLE_STATUSES = ['approved', 'completed', 'posted'];

/**
 * Whether a history row offers a promo Redeem action, shows Redeemed, or
 * neither. Mirrors the front office and Unity clients: Redeem only while the
 * deposit is unclaimed, unexpired and settled; Redeemed once claimed; nothing
 * for expired / ineligible rows (the backoffice sends -1 for non-deposits).
 */
export function getRedeemState(tx: {
  isClaimed?: number;
  isExpired?: number;
  status?: string;
}): RedeemState {
  const status = tx.status?.toLowerCase();
  // The claim endpoint only accepts approved, completed or posted deposits (a
  // row with no status is the ledger's settled default), so any other status
  // shows neither action, even if it is flagged claimed.
  if (status && !CLAIMABLE_STATUSES.includes(status)) return null;
  if (Number(tx.isClaimed) === 1) return 'redeemed';
  if (Number(tx.isClaimed) === 0 && Number(tx.isExpired) === 0) return 'redeem';
  return null;
}

/**
 * `outcomeUnknown` means we cannot tell whether the claim committed server-side
 * (timeout, connection failure, unreadable reply, or a 5xx). It is never a safe
 * rejection: the caller must not offer another claim for the deposit.
 */
export type PromoClaimResult = { success: boolean; message: string; outcomeUnknown?: boolean };

/**
 * Claims made by this player, by deposit. The registry outlives the modal and
 * the History view, so unmounting (for example by switching Cashier tabs) cannot
 * reopen a deposit to a second claim.
 *  - pending: request in flight.
 *  - unknown: the outcome could not be read; it may have committed.
 *  - claimed: the backend confirmed it; shown as Redeemed even if a lagging
 *    ledger read still reports the old flags.
 * A readable rejection removes the deposit. localStorage is the source of truth,
 * read fresh on every access and updated one deposit at a time, so a reload and
 * other tabs see the same entries, including removals (a pending entry found by
 * a freshly loaded page is treated as unknown). A claim writes a token and
 * re-reads it so two tabs cannot both acquire the same deposit. When storage is
 * unavailable or not writable the registry falls back to this page's memory.
 * Entries are released by a fresh ledger read (see reconcileClaims).
 *
 * localStorage has no atomic compare-and-set, so this only narrows the cross-tab
 * race. The backoffice claim is what prevents a double credit: it takes the
 * deposit with a conditional update before paying, so a second claim for the
 * same deposit is rejected.
 */
type ClaimEntry = { state: 'pending' | 'unknown' | 'claimed'; at: number; token?: string };
type ClaimMap = Map<string, ClaimEntry>;

const CLAIMS_STORAGE_KEY = 'hijack.cashier.promoClaims.v1';

// Fallback registry for when localStorage cannot be read or written. While the
// last write failed it is the source of truth, because a read that still
// succeeds would return the stale stored data and discard the in-memory claim.
let memoryClaims: ClaimMap = new Map();
let storageWritable = true;

const readClaims = (): ClaimMap => {
  if (!storageWritable) return new Map(memoryClaims);
  try {
    const raw = window.localStorage.getItem(CLAIMS_STORAGE_KEY);
    const stored = raw ? JSON.parse(raw) : {};
    const claims: ClaimMap = new Map();
    Object.entries(stored).forEach(([id, entry]: [string, any]) => {
      if (entry?.state === 'pending' || entry?.state === 'unknown' || entry?.state === 'claimed') {
        claims.set(id, { state: entry.state, at: Number(entry.at) || Date.now(), token: entry.token });
      }
    });
    memoryClaims = claims;
    return claims;
  } catch {
    return new Map(memoryClaims);
  }
};

const writeClaims = (claims: ClaimMap): void => {
  memoryClaims = new Map(claims);
  try {
    window.localStorage.setItem(CLAIMS_STORAGE_KEY, JSON.stringify(Object.fromEntries(claims)));
    storageWritable = true;
  } catch {
    // The in-memory registry still protects this page load; every later write
    // retries storage and returns to it once it works again.
    storageWritable = false;
  }
};

// A pending entry found when this page loads belongs to a page that is gone; its
// request may have committed, so it becomes unknown.
const restoreClaimsOnLoad = (): void => {
  const claims = readClaims();
  let changed = false;
  claims.forEach((entry, id) => {
    if (entry.state === 'pending') {
      claims.set(id, { state: 'unknown', at: entry.at });
      changed = true;
    }
  });
  if (changed) writeClaims(claims);
};
restoreClaimsOnLoad();

// Read-modify-write one deposit so another tab's entries are never overwritten.
const setClaim = (transactionId: string, state: ClaimEntry['state'], token?: string): void => {
  const claims = readClaims();
  claims.set(transactionId, { state, at: Date.now(), token });
  writeClaims(claims);
};

const clearClaim = (transactionId: string): void => {
  const claims = readClaims();
  if (claims.delete(transactionId)) writeClaims(claims);
};

/**
 * Take the claim for a deposit: write a token, then re-read storage to confirm
 * this tab's token is the one that stuck. False when another tab holds it.
 */
const acquireClaim = (transactionId: string): boolean => {
  if (readClaims().has(transactionId)) return false;
  const token = Math.random().toString(36).slice(2);
  setClaim(transactionId, 'pending', token);
  return readClaims().get(transactionId)?.token === token;
};

export const getClaimState = (transactionId: string): ClaimEntry['state'] | null =>
  readClaims().get(transactionId)?.state ?? null;

/**
 * Reconcile claims with an authoritative ledger read. A deposit the ledger shows
 * claimed or no longer claimable needs no lock. An unknown claim on a deposit the
 * ledger still shows open did not commit, or is still running and will be
 * rejected server-side if the player claims again, so Redeem is offered again.
 * A confirmed claim stays Redeemed through a lagging read, and in-flight claims
 * are left alone.
 */
export function reconcileClaims(
  rows: Array<{ transactionId: string; isClaimed?: number; isExpired?: number }>
): void {
  const claims = readClaims();
  let changed = false;
  rows.forEach((row) => {
    const entry = claims.get(row.transactionId);
    if (!entry || entry.state === 'pending') return;
    const stillClaimable = Number(row.isClaimed) === 0 && Number(row.isExpired) === 0;
    if (!stillClaimable || entry.state === 'unknown') {
      claims.delete(row.transactionId);
      changed = true;
    }
  });
  if (changed) writeClaims(claims);
}

/** Test seam: the registry is module state, so tests reset it between cases. */
export const resetSessionClaims = (): void => writeClaims(new Map());

/**
 * Interpret the `/api/promo/claim` payload. The backoffice wraps the claim in
 * rc '0' and reports the real outcome in `data.status` ('Valid' / 'Invalid')
 * with the player-facing text in `data.message`.
 */
export function interpretClaimResponse(json: any): PromoClaimResult {
  const message: string = json?.data?.message || json?.message || '';
  const ok = json?.rc === '0' || json?.rc === 0;
  if (ok && json?.data?.status === 'Valid') {
    return { success: true, message: message || 'Promo code claimed' };
  }
  if (ok && json?.data?.status === 'Invalid') {
    // Show the backend's reason (already claimed, window closed, ...) when it sent one.
    return { success: false, message: message || PROMO_PROBLEM_MESSAGE };
  }
  // Any other shape does not show the backend declined the claim; it may have
  // credited the reward before the reply went wrong, so the outcome is unknown.
  return { success: false, outcomeUnknown: true, message: PROMO_CONNECTION_MESSAGE };
}

/**
 * Claim a promo code against an earlier deposit. Never throws. Only a readable
 * backend reply is a definitive result; anything else is `outcomeUnknown`.
 */
export async function claimPromoCode(params: {
  playerGUID: string;
  promoCode: string;
  transactionId: string;
  jwt: string;
}): Promise<PromoClaimResult> {
  const { playerGUID, promoCode, transactionId, jwt } = params;
  if (!acquireClaim(transactionId)) {
    return { success: false, outcomeUnknown: true, message: PROMO_CONNECTION_MESSAGE };
  }
  try {
    const { status, json } = await postJsonWithTimeout(`${getBackofficeUrl()}/api/promo/claim`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`
      },
      body: JSON.stringify({
        playerGUID,
        promoCode: promoCode.trim(),
        transactionId,
        token: jwt
      })
    });
    // A 5xx may have failed after the credit was written, so it is not a rejection.
    if (status >= 500) {
      setClaim(transactionId, 'unknown');
      return { success: false, outcomeUnknown: true, message: PROMO_CONNECTION_MESSAGE };
    }
    // A 4xx (for example an expired session) was refused before the claim ran,
    // even when the body is empty or not JSON. A 408 is a timeout and stays unknown.
    if (status >= 400 && status !== 408) {
      clearClaim(transactionId);
      return { success: false, message: PROMO_PROBLEM_MESSAGE };
    }
    if (status === 408) {
      setClaim(transactionId, 'unknown');
      return { success: false, outcomeUnknown: true, message: PROMO_CONNECTION_MESSAGE };
    }
    const result = interpretClaimResponse(json);
    if (result.success) setClaim(transactionId, 'claimed');
    else if (result.outcomeUnknown) setClaim(transactionId, 'unknown');
    else clearClaim(transactionId);
    return result;
  } catch {
    setClaim(transactionId, 'unknown');
    return { success: false, outcomeUnknown: true, message: PROMO_CONNECTION_MESSAGE };
  }
}

/**
 * POST the code to the backoffice verifier and return the normalised verdict.
 * Network / parse failures resolve to an invalid verdict with a friendly
 * message rather than throwing, so the caller only ever has to branch on
 * `valid`.
 */
export async function verifyPromoCode(params: {
  playerGUID: string;
  promoCode: string;
  amount: number;
  jwt: string;
}): Promise<PromoVerifyResult> {
  const { playerGUID, promoCode, amount, jwt } = params;
  try {
    const { json } = await postJsonWithTimeout(`${getBackofficeUrl()}/api/promo/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`
      },
      body: JSON.stringify({
        playerGUID,
        isPromoCode: 1,
        promoCode: promoCode.trim(),
        amount
      })
    });
    return interpretPromoResponse(json);
  } catch {
    return {
      valid: false,
      message: PROMO_CONNECTION_MESSAGE,
      rewards: null,
      minDeposit: null
    };
  }
}
